import { pbClient } from './pbClient'
import { resolveCollectionName } from './collectionMapping'
import { runtimeEnv } from './runtimeEnv'

const PERFORMANCE_COLLECTION_KEY = 'performance_metrics'
const DEFAULT_FLUSH_INTERVAL_MS = 20000
const MAX_BUFFER_SIZE = 150

interface PerformanceMetricPayload {
  user?: string
  metric: string
  value: number
  unit: string
  source: string
  tags?: string
  timestamp: string
  appVersion: string
  environment: string
}

class PerformanceMonitorService {
  private buffer: PerformanceMetricPayload[] = []
  private flushTimer: ReturnType<typeof setInterval> | null = null
  private initialized = false
  private isFlushing = false
  private active = true
  private collectionName = resolveCollectionName(PERFORMANCE_COLLECTION_KEY)

  initialize(): void {
    if (this.initialized) return
    if (!runtimeEnv.isFeatureEnabled('performanceMonitoring')) return

    this.initialized = true
    this.collectionName = resolveCollectionName(PERFORMANCE_COLLECTION_KEY)

    this.captureNavigationTiming()
    this.observeLargestContentfulPaint()

    this.flushTimer = setInterval(() => {
      void this.flush()
    }, DEFAULT_FLUSH_INTERVAL_MS)
  }

  shutdown(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = null
    }
    void this.flush()
  }

  recordTiming(metric: string, durationMs: number, source = 'app', tags?: Record<string, unknown>): void {
    if (!runtimeEnv.isFeatureEnabled('performanceMonitoring')) return

    const payload: PerformanceMetricPayload = {
      user: pbClient.getAuthUser()?.id,
      metric,
      value: Number(durationMs.toFixed(2)),
      unit: 'ms',
      source,
      tags: tags ? this.safeSerialize(tags) : undefined,
      timestamp: new Date().toISOString(),
      appVersion: runtimeEnv.getEnvironment().appVersion,
      environment: runtimeEnv.getEnvironment().appEnv,
    }

    this.push(payload)
  }

  recordGauge(metric: string, value: number, source = 'app', tags?: Record<string, unknown>): void {
    if (!runtimeEnv.isFeatureEnabled('performanceMonitoring')) return

    const payload: PerformanceMetricPayload = {
      user: pbClient.getAuthUser()?.id,
      metric,
      value,
      unit: 'count',
      source,
      tags: tags ? this.safeSerialize(tags) : undefined,
      timestamp: new Date().toISOString(),
      appVersion: runtimeEnv.getEnvironment().appVersion,
      environment: runtimeEnv.getEnvironment().appEnv,
    }

    this.push(payload)
  }

  async flush(): Promise<void> {
    if (!this.initialized || !this.active || this.isFlushing || this.buffer.length === 0) return

    this.isFlushing = true

    try {
      const batch = this.buffer.splice(0, this.buffer.length)
      await Promise.all(
        batch.map(async (payload) => {
          try {
            await pbClient.createRecord(this.collectionName, payload as unknown as Record<string, unknown>)
          } catch (error) {
            if (this.isPermanentCollectionFailure(error)) {
              this.disableChannel(this.describeFailure(error))
              return
            }

            // Transient issue: keep app stable and retry later.
            this.buffer.push(payload)
          }
        })
      )
    } finally {
      this.isFlushing = false
    }
  }

  getBufferSize(): number {
    return this.buffer.length
  }

  private push(payload: PerformanceMetricPayload): void {
    if (!this.active) return

    this.buffer.push(payload)

    if (this.buffer.length > MAX_BUFFER_SIZE) {
      this.buffer.shift()
    }

    if (navigator.onLine) {
      void this.flush()
    }
  }

  private captureNavigationTiming(): void {
    if (typeof performance === 'undefined') return

    const entries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]
    const nav = entries[0]
    if (!nav) return

    this.recordTiming('navigation.total', nav.duration, 'navigation')
    this.recordTiming('navigation.domContentLoaded', nav.domContentLoadedEventEnd, 'navigation')
    this.recordTiming('navigation.loadEvent', nav.loadEventEnd, 'navigation')
  }

  private observeLargestContentfulPaint(): void {
    if (typeof PerformanceObserver === 'undefined') return

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        const last = entries[entries.length - 1]
        if (!last) return
        this.recordTiming('webvitals.lcp', last.startTime, 'webvitals')
      })

      observer.observe({ type: 'largest-contentful-paint', buffered: true })
    } catch {
      // Some browsers do not support this metric.
    }
  }

  private safeSerialize(input: unknown): string {
    try {
      return JSON.stringify(input)
    } catch {
      return '{"error":"serialize_failed"}'
    }
  }

  private isPermanentCollectionFailure(error: unknown): boolean {
    const statusCode = this.getStatusCode(error)
    return statusCode === 403 || statusCode === 404
  }

  private getStatusCode(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) return undefined
    if ('statusCode' in error) {
      return Number((error as { statusCode?: unknown }).statusCode)
    }
    return undefined
  }

  private describeFailure(error: unknown): string {
    const status = this.getStatusCode(error)
    if (status === 404) {
      return `Collection '${this.collectionName}' not found`
    }
    if (status === 403) {
      return `Collection '${this.collectionName}' forbidden by rules`
    }
    return `Performance channel disabled due to persistent failure on '${this.collectionName}'`
  }

  private disableChannel(reason: string): void {
    if (!this.active) return
    this.active = false
    this.buffer = []

    if (runtimeEnv.shouldLog('warn')) {
      console.warn('[Performance] Disabled performance channel:', reason)
    }
  }
}

export const performanceMonitor = new PerformanceMonitorService()
export default performanceMonitor
